<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { componentStore } from "@common/componentStore/componentStore";
import { actionStore } from "@common/actionStore";
import { library } from "@common/library/library";
import { SkeletonComponent } from "@syncfusion/ej2-vue-notifications";

const runOnceActions = {
    // Put actions in only
};
const setIncludeWhenLogicProps = (pObjLabel) => ({
    type: 'logicEditor',
    otherProps: {
        hideStandardRecordTypes: true,
        recordTypes: [
            { text: 'Record', value: 'object2' },
            { text: pObjLabel, value: 'object1' },
            { text: 'Previous Item', value: 'object3' },
            { text: 'Next Item', value: 'object4' },
        ],
        otherColumns: [
            {
                field: 'complete',
                label: 'Item: complete',
                type: 'boolean',
                category: 'Item Props',
                operators: 'booleanOperators'
            }, {
                field: 'enable',
                label: 'Item: enable',
                type: 'boolean',
                category: 'Item Props',
                operators: 'booleanOperators'
            }, {
                field: 'include',
                label: 'Item: include',
                type: 'boolean',
                category: 'Item Props',
                operators: 'booleanOperators'
            }
        ],
        defaultRecordType: 'object2'
    },
    default: []
});

const fontList = [
    { text: 'Proxima Nova', value: 'proxima-nova' },
    { text: 'Open Sans', value: 'Open Sans' },
    { text: 'Source Serif Pro', value: 'Source Serif Pro' },
    { text: 'Schoolbook', value: 'Schoolbook' },
    { text: 'Arial', value: 'Arial' },
    { text: 'Times New Roman', value: 'Times New Roman' },
    { text: 'Courier New', value: 'Courier New' },
    { text: 'Comic Sans MS', value: 'Comic Sans MS' },
    { text: 'Brush Script MT', value: 'Brush Script MT' },
];

const fontWeights = [
    { text: 'Light 300', value: '300' },
    { text: 'Regular 400', value: '400' },
    { text: 'Semi-bold 600', value: '600' },
    { text: 'Bold 700', value: '700' },
    { text: 'Extra-bold 800', value: '800' },
];

const fieldsObj = {
    text: 'text',
    value: 'value'
};

const editorSettings = {
    generalSettingsPanel: {
        type: 'panel',
        label: 'General Settings',
        id: 'generalSettingsPanel',
        fields: [
            'datasetIds',
            'parentRecordType',
            'sortBy',
            'sortField',
            'sortDirection',
            'groupItemsBy',
            'customGroupHeaders',
            'txtGroupHeading',
            'noRecordsMessage',
            'allowDragAndDrop',
            'hideWhenNoItems',
            'itemListHeight',
            'visibleItemCount',
            'showPaginationInHeader',
            'showPaginationAtTop',
            'itemIconType',
            'itemIconSize',
            'avatarContactCardId',
            'iconStyles'
        ]
    },
    headerSettings: {
        type: 'panel',
        label: 'Header Settings',
        id: 'headerSettings',
        fields: [
            'header',
            'hideListHeader',
            'headerStyles'
        ]
    },
    filterSettings: {
        type: 'panel',
        label: 'Filter Settings',
        id: 'filterSettings',
        fields: [
            'enableFilter',
            'filterOptionsList',
            'additionalConfig'
        ]
    },
    itemListPanel: {
        type: 'panel',
        label: 'Item Settings',
        id: 'itemListPanel',
        fields: [
            'itemsListSource',
            'itemList'
        ]
    },
    enableFilter: {
        label: 'Enable Search Filter',
        type: 'boolean',
        id: 'enableFilter',
        helpText: 'Display a configurable search filter at the top of the item list.'
    },
    filterOptionsList: {
        label: 'Filter Settings',
        type: 'array',
        id: 'filterSettings',
        helpText: 'Settings for the search filter',
        default: [],
        itemConfig: {
            itemType: 'object',
            primaryKey: 'filterKey',
            permissions: {
                canMove: true,
                canDelete: true,
                allowAddItem: true
            },
            keys: {
                filterKey: {
                    type: 'string',
                    label: 'Key',
                    id: 'filterKey',
                    helpText: 'The key in your record object you want this filter to query.'
                },
                label: {
                    type: 'string',
                    label: 'Label',
                    id: 'label',
                    helpText: 'The label that is displayed in the filter list.'
                },
                type: {
                    type: 'dropdown',
                    label: 'Type',
                    id: 'type',
                    items: [
                        { text: 'String', value: 'string' },
                        { text: 'Dropdown', value: 'dropdown' },
                        { text: 'Date', value: 'date' },
                    ],
                    fields: {
                        text: 'text',
                        value: 'value'
                    },
                    helpText: 'Type of control to use for the filter.'
                }
            },
            newItem: {
                filterKey: 'someProperty',
                label: 'New Filter',
                type: 'string'
            }
        },
        logic: [
            {
                record: 'self',
                criteriaRecord: 'component',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.enableFilter.value"
            }
        ]
    },
    additionalConfig: {
        label: 'Filter Additional Config',
        type: 'object',
        id: 'additionalConfig',
        helpText: 'Condensed filter additional config.',
        default: {},
        properties: {
            showResultsAfterFiltersApplied: {
                type: 'boolean',
                label: 'Hide Results Until Filter is Applied',
                id: 'showResultsAfterFiltersApplied',
                helpText: 'Do not show item list items until a filter is applied.'
            }
        }
    },
    groupItemsBy: {
        id: 'groupItemsBy',
        type: 'dropdowntree',
        itemsSource: 'configStore.allFieldOptions()',
        allowFiltering: true,
        filterType: 'Contains',
        label: 'Group Items By',
    },
    customGroupHeaders: {
        type: 'boolean',
        id: 'customGroupHeaders',
        label: 'Use Custom Group Header',
        default: false,
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.groupItemsBy.value.length"
            }
        ],
        // criteria: "this.controls.itemList.value[__index__].groupItemsBy"
    },
    txtGroupHeading: {
        type: 'text',
        id: 'txtGroupHeading',
        label: 'HTML Group Heading',
        showExpandMinimizeBtns: true,
        default: '<p>Group Header</p>',
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.customGroupHeaders.value"
            }
        ],
    },
    allowDragAndDrop: {
        type: 'boolean',
        label: 'Allow Drag and Drop',
        helpText: 'Allow drag and drop.',
        id: 'allowDragAndDrop',
        default: false
    },
    header: {
        type: 'string',
        label: 'Item List Header',
        helpText: 'Heading at the top of the itemList.',
        id: 'itemListheader',
        default: '<p>An Item List</p>',
    },
    hideListHeader: {
        type: 'boolean',
        label: 'Hide Series Header',
        id: 'hideListHeader',
        helpText: 'Hide the title that displays above the itemList.',
        default: false
    },
    headerStyles: {
        type: 'object',
        label: 'Header Styles',
        helpText: 'Select various styles for your header.',
        id: 'headerStyles',
        default: {
            backgroundColor: 'ffffffff',
            color: '000000ff',
            fontFamily: 'proxima-nova, sans-serif',
            fontSize: '20px',
            fontWeight: '600',
            textAlign: 'center'
        },
        properties: {
            backgroundColor: {
                type: 'color',
                label: 'Header Background Color',
                helpText: 'Color for the header background.',
                id: 'backgroundColor',
                noColor: true
            },
            color: {
                type: 'color',
                label: 'Header Text Color',
                helpText: 'Color for the header text.',
                id: 'color'
            },
            fontSize: {
                type: 'dropdown',
                label: 'Header Text Size',
                helpText: 'Color for the header text.',
                fields: fieldsObj,
                items: [
                    { text: '16px', value: '16px' },
                    { text: '18px', value: '18px' },
                    { text: '20px', value: '20px' },
                    { text: '24px', value: '24px' },
                    { text: '28px', value: '28px' },
                    { text: '36px', value: '36px' }
                ],
                id: 'headerFontSize',
                default: 'Header Font Size'
            },
            fontFamily: {
                type: 'dropdown',
                label: 'Title Font',
                helpText: 'The font type used for the header text.',
                fields: fieldsObj,
                items: fontList,
                id: 'fontFamily'
            },
            fontWeight: {
                type: 'dropdown',
                label: 'Title Weight',
                helpText: 'The weight of the header text.',
                fields: fieldsObj,
                items: fontWeights,
                id: 'fontWeight'
            },
            textAlign: {
                type: 'dropdown',
                label: 'Text Alignment',
                id: 'textAlign',
                default: 'center',
                helpText: 'Text alignment for the chart title: left, right, center.',
                fields: fieldsObj,
                items: [
                    { text: 'Center', value: 'center' },
                    { text: 'Align Left', value: 'left' },
                    { text: 'Align Right', value: 'right' },
                ]
            },
        }
    },
    datasetIds: {
        type: 'datasource',
        id: 'datasetIds',
        mode: 'Box',
        itemsSource: 'configStore.getDatasourceList()',
        fields: {
            text: 'text',
            value: 'value',
            parentValue: 'pId',
            hasChildren: 'hasChild'
        },
        allowFiltering: true,
        default: [],
        label: "Select a datasource",
        filterType: 'contains',
        // onchange: "selectDataset",
        helpText: 'Dataset that provides data for this item list',
        secondaryActions: [{
            text: 'Dataset Details',
            action: 'viewDataset',
            source: 'actionStore',
            params: {}
        }]
    },
    parentRecordType: {
        type: 'dropdown',
        label: 'Enable Parent Records',
        items: [
            { text: 'Context Volunteer', value: 'volId' },
            { text: 'Context Opportunity', value: 'oppId' },
            { text: 'Context Organization', value: 'orgId' },
            { text: 'Mapped Parent Records', value: 'parentRecords' },
        ],
        fields: {
            text: 'text',
            value: 'value'
        },
        id: 'parentRecordType',
        helpText: 'Use the record passed into the component from external source.'
    },
    hideWhenNoItems: {
        label: 'Hide this component when there are no visible items',
        type: 'boolean',
        id: 'hideWhenNoItems',
        helpText: 'This setting will hide the component when there are no items after all rendering. This is not affected by search returning no results',
        default: false
    },
    itemsListSource: {
        label: 'Source for Items',
        type: 'dropdown',
        items: [
            { text: 'User Configured', value: 'user_configured' },
            { text: 'itemsList Property', value: 'itemsList_prop' },
        ],
        fields: {
            text: 'text',
            value: 'value'
        },
        id: 'itemsListSource',
        helpText: 'Define the source for your items. If you choose "user Configured" you will be able to configure your own items.  If you choose "Items Property" the items will come from the itemList\'s itemsList property.'
    },
    itemList: {
        label: 'Items',
        id: 'itemList',
        type: 'ARRAY',
        default: [],
        helpText: 'Create a list of items that will be included and enabled for each record according to the include and enable property logical conditions.  Each item can have a title, description, one or more actions, and logical criteria for when a item is enabled and included',
        itemConfig: {
            itemType: 'object',
            primaryKey: 'humanId',
            permissions: {
                canMove: true,
                canDelete: true,
                allowAddItem: true
            },
            keys: {
                humanId: {
                    type: 'string',
                    default: 'A Item',
                    id: 'humanId',
                    label: 'Item ID',
                    helpText: 'Give your item an ID so it is easy to recognize in the editor.'
                },
                // include: {
                //     type: 'BOOLEAN',
                //     default: true,
                //     id: 'include',
                //     label: 'Include by Default?',
                //     helpText: 'Should the item be included by default?'
                // },
                enable: {
                    type: 'BOOLEAN',
                    default: true,
                    id: 'enable',
                    label: 'Enable by Default?',
                    helpText: 'Should the item be enabled by default?'
                },
                complete: {
                    type: 'BOOLEAN',
                    default: false,
                    id: 'complete',
                    label: 'Complete by Default?',
                    helpText: 'Should the item be enabled by default?'
                },
                includeWhen: {
                    label: 'Display Item Logic',
                    id: 'includeWhen',
                    helpText: 'Set logic to determine when the item should be displayed in the list.',
                    ...setIncludeWhenLogicProps('List Item')
                },
                enableWhen: {
                    label: 'Enable Item Logic',
                    helpText: 'Set logic to determine when the item should be enabled in the list. Enabled items can show actions.',
                    id: 'enableWhen',
                    ...setIncludeWhenLogicProps('List Item')
                },
                activeWhen: {
                    label: 'Item is Active When',
                    helpText: 'Set logic to determine when the item should be active in the list. Active items show actions.',
                    id: 'activeWhen',
                    ...setIncludeWhenLogicProps('List Item')
                },
                completeWhen: {
                    label: 'Item is Complete When',
                    helpText: 'Set logic to determine when the item should marked as completed in the list.',
                    id: 'completeWhen',
                    ...setIncludeWhenLogicProps('List Item')
                },
                titles: {
                    type: 'ARRAY',
                    label: 'Item Titles',
                    id: 'titles',
                    itemConfig: {
                        primaryKey: 'titleLabel',
                        itemType: 'object',
                        permissions: {
                            canMove: true,
                            canDelete: true,
                            allowAddItem: true
                        },
                        keys: {
                            titleLabel: {
                                type: 'string',
                                id: 'titleLabel',
                                label: 'Title Label',
                                helpText: 'Label of the title for the settingsEditor UI.'
                            },
                            template: {
                                type: 'string',
                                id: 'template',
                                label: 'Title Text Template',
                                helpText: 'Text template for the title of the item.'
                            },
                            // include: {
                            //     label: 'Display Title Default',
                            //     id: 'include',
                            //     type: 'BOOLEAN',
                            //     helpText: 'Set whether the title is displayed by default. Turn this on if no title display logic is used.',
                            //     default: true
                            // },
                            hideWhenDisabled: {
                                label: 'Hide When Item Not Active',
                                id: 'hideWhenDisabled',
                                type: 'boolean',
                                default: false,
                                helpText: 'Hide the title when the step is not active. Note: Action Logic overrides this.'
                            },
                            hideWhenComplete: {
                                label: 'Hide When Item is Complete',
                                id: 'hideWhenComplete',
                                type: 'boolean',
                                default: false,
                                helpText: 'Hide the title when the step is complete. Note: Action Logic overrides this.'
                            },
                            includeWhen: {
                                label: 'Title Display Logic',
                                id: 'includeWhen',
                                helpText: 'Set logic to determine when the title should be displayed.',
                                ...setIncludeWhenLogicProps('Title')
                            }
                        },
                        newItem: {
                            titleLabel: 'Item Title',
                            template: '<strong>Title</strong>',
                            templateId: false,
                            // include: true,
                            hideWhenDisabled: false,
                            hideWhenComplete: false,
                            includeWhen: []
                        }
                    },
                    helpText: 'The title(s) for the item.'
                },
                descriptions: {
                    type: 'ARRAY',
                    id: 'descriptions',
                    label: 'Item Descriptions',
                    helpText: 'A item can have one ore multiple descriptions that are displayed based on logic.',
                    itemConfig: {
                        primaryKey: 'descriptionLabel',
                        itemType: 'object',
                        permissions: {
                            canMove: true,
                            canDelete: true,
                            allowAddItem: true
                        },
                        keys: {
                            descriptionLabel: {
                                type: 'string',
                                id: 'descriptionLabel',
                                label: 'Description Title',
                                helpText: 'Text you want to display in the description area.',
                                default: ''
                            },
                            template: {
                                type: 'text',
                                id: 'template',
                                label: 'HTML Template',
                                showExpandMinimizeBtns: true,
                                helpText: 'HTML template for the information you want to display in the description area. Click HERE for examples.',
                                default: '<p>Description</p>'
                            },
                            // include: {
                            //     label: 'Default Include',
                            //     id: 'include',
                            //     type: 'BOOLEAN',
                            //     default: true
                            // },
                            hideWhenDisabled: {
                                label: 'Hide When Item Not Active',
                                id: 'hideWhenDisabled',
                                type: 'boolean',
                                default: false,
                                helpText: 'Hide the description when the step is not active. Note: Action Logic overrides this.'
                            },
                            hideWhenComplete: {
                                label: 'Hide When Item is Complete',
                                id: 'hideWhenComplete',
                                type: 'boolean',
                                default: true,
                                helpText: 'Hide the description when the step is complete. Note: Action Logic overrides this.'
                            },
                            includeWhen: {
                                label: 'Include When',
                                id: 'includeWhen',
                                helpText: 'Set logic to determine when this description should be displayed.',
                                ...setIncludeWhenLogicProps('Description')
                            }
                        },
                        newItem: {
                            descriptionLabel: 'A description',
                            template: '<p>Description</p>',
                            templateId: false,
                            // include: true,
                            hideWhenDisabled: true,
                            hideWhenComplete: true,
                            includeWhen: []
                        }
                    }
                },
                actions: {
                    type: 'ARRAY',
                    id: 'actions',
                    label: 'Item Actions',
                    helpText: 'A item can have one ore multiple actions that are displayed based on logic.',
                    itemConfig: {
                        primaryKey: 'label',
                        itemType: 'object',
                        permissions: {
                            canMove: true,
                            canDelete: true,
                            allowAddItem: true
                        },
                        keys: {
                            label: {
                                type: 'string',
                                id: 'label',
                                label: 'Button Label',
                                helpText: 'The label for your action button.',
                                default: 'Action',
                            },
                            actionIcon: {
                                label: 'Action button icon',
                                type: 'iconpicker',
                                id: 'actionIcon',
                                helpText: 'Select an icon from the dropdown.'
                            },
                            actionTextColor: {
                                type: 'color',
                                label: 'Action button text color',
                                helpText: 'Color for the Text of the Action button.',
                                id: 'actionTextColor',
                                default: '#000000',
                            },
                            actionBackgroundColor: {
                                type: 'color',
                                label: 'Action button background color',
                                helpText: 'Color for the Background of the Action button.',
                                id: 'actionBackgroundColor',
                                default: '#ffffff',
                            },
                            // include: {
                            //     label: 'Display Default',
                            //     id: 'include',
                            //     type: 'BOOLEAN',
                            //     helpText: 'Set whether you want the action button to display, or not, by default. Set this to true if you do not plan to use action display logic',
                            //     default: true
                            // },
                            hideWhenDisabled: {
                                label: 'Hide When Item Not Active',
                                id: 'hideWhenDisabled',
                                type: 'boolean',
                                default: true,
                                helpText: 'Hide the action button when the step is not active. Note: Action Logic overrides this.'
                            },
                            hideWhenComplete: {
                                label: 'Hide When Item is Complete',
                                id: 'hideWhenComplete',
                                type: 'boolean',
                                default: true,
                                helpText: 'Hide the action button when the step is complete. Note: Action Logic overrides this.'
                            },
                            includeWhen: {
                                label: 'Action Display Logic',
                                id: 'includeWhen',
                                helpText: 'Set logic to determine when the action button should be displayed.',
                                ...setIncludeWhenLogicProps('Action')
                            },
                            disableWhen: {
                                label: 'Item is Disabled When',
                                helpText: 'Set logic to determine when the action should be disabled.',
                                id: 'disableWhen',
                                ...setIncludeWhenLogicProps('List Item')
                            },
                            action: {
                                label: 'Action to Execute',
                                id: 'action',
                                type: 'actionfield',
                                itemsSource: 'actionStore.getActionOptions()',
                                fields: { text: 'text', value: 'value' },
                                helpText: 'Choose from a list of standard actions that you want to execute when a user clicks the action button.',
                                default: {
                                    action: '',
                                    params: {}
                                }
                            },
                            message: {
                                label: 'Action Message',
                                id: 'message',
                                type: 'string',
                                helpText: 'Display a message below the action button.',
                                default: ''
                            }
                        },
                        newItem: {
                            label: 'Click',
                            message: 'A message under the action',
                            actionAutomationId: false,
                            actionBCPackageId: false,
                            action: {
                                action: '',
                                params: {}
                            },
                            // include: true,
                            hideWhenDisabled: true,
                            hideWhenComplete: true,
                            includeWhen: [],
                            disableWhen: [],
                        }
                    }
                },
                itemActionDisplay: {
                    label: 'Display Action Buttons in a Row',
                    id: 'itemActionDisplay',
                    type: 'dropdown',
                    helpText: 'When turned on, the buttons will display in a row rather than a column (which is the default).',
                    fields: fieldsObj,
                    items: [
                        { text: 'default', value: '' },
                        { text: 'inline', value: 'action-btn-inline' }
                    ]
                },
                actionBorderRadius: {
                    type: 'string',
                    label: 'Action Button Border Radius',
                    id: 'actionBorderRadius',
                    default: '0',
                    helpText: 'Type in a number between 1-20 ending with a px (ie: 5px).'
                },
                enableEvenOddColors: {
                    label: 'Use Even/Odd Background Colors',
                    id: 'enableEvenOddColors',
                    type: 'BOOLEAN',
                    helpText: 'Set whether you want to use Even/Odd Background Colors or not.',
                    default: true
                },
                evenItemBackgroundColor: {
                    type: 'color',
                    label: 'Even Item Background Color',
                    helpText: 'Color for the Background.',
                    id: 'evenItemBackgroundColor',
                    default: '#FFFFFFFF',
                    logic: [
                        {
                            record: 'self',
                            criteriaRecord: 'component',
                            prop: 'display',
                            valueType: 'criteria',
                            criteria: "this.controls.itemList.value[__index__].enableEvenOddColors"
                        }
                    ]
                },
                oddItemBackgroundColor: {
                    type: 'color',
                    label: 'Odd Item Background Color',
                    helpText: 'Color for the Background.',
                    id: 'oddItemBackgroundColor',
                    default: '#EEEEEEFF',
                    logic: [
                        {
                            record: 'self',
                            criteriaRecord: 'component',
                            prop: 'display',
                            valueType: 'criteria',
                            criteria: "this.controls.itemList.value[__index__].enableEvenOddColors"
                        }
                    ]
                },
                itemBackgroundColor: {
                    type: 'color',
                    label: 'itemList Background Color',
                    helpText: 'Color for the Background.',
                    id: 'itemBackgroundColor',
                    default: '#ecececff',
                },
                itemCompleteColor: {
                    type: 'color',
                    label: 'Color signifying an item is Complete.',
                    helpText: 'Background color for Complete.',
                    id: 'itemCompleteColor',
                    default: '#efffec',
                },
                itemActiveColor: {
                    type: 'color',
                    label: 'Color signifying an item is Active.',
                    helpText: 'Background color for Active.',
                    id: 'itemActiveColor',
                    default: '#d8f9ff',
                },
                itemDisabledColor: {
                    type: 'color',
                    label: 'Color signifying an item is Disabled.',
                    helpText: 'Background color for Disabled.',
                    id: 'itemDisabledColor',
                    default: '#555555',
                },
            },
            newItem: {
                humanId: 'item_1',
                titles: [
                    {
                        titleLabel: 'Item Title',
                        template: '<strong>Title</strong>',
                        templateId: false,
                        // include: true,
                        includeWhen: []
                    }
                ],
                descriptions: [
                    {
                        descriptionLabel: 'A description',
                        template: '<p>Description</p>',
                        templateId: false,
                        // include: true,
                        includeWhen: []
                    }
                ],
                actions: [
                    {
                        label: 'Click',
                        message: 'A message under the action',
                        actionAutomationId: false,
                        actionBCPackageId: false,
                        action: {
                            action: '',
                            params: {}
                        },
                        // include: true,
                        hideWhenDisabled: true,
                        hideWhenComplete: true,
                        includeWhen: []
                    }
                ],
                // include: true,
                enable: true,
                includeWhen: [],
                enableWhen: [],
                evenItemBackgroundColor: '#FFFFFFFF',
                oddItemBackgroundColor: '#EEEEEEFF',
                itemBackgroundColor: '#ecececff',
                itemCompleteColor: '#efffec',
                // itemActiveColor: '#d8f9ff',
                // itemDisabledColor: '#555555'
            }
        },
        logic: [
            {
                record: 'self',
                criteriaRecord: 'component',
                prop: 'display',
                valueType: 'criteria',
                criteria: "!this.controls.itemsListSource.value || this.controls.itemsListSource.value === 'user_configured'"
            }
        ]
    },
    itemListHeight: {
        id: 'itemListHeight',
        type: 'dropdown',
        fields: fieldsObj,
        label: 'Max Height for the item-list-container. Anything beyond will have a scrollbar.',
        helpText: 'Choose a height between 300-1000px, 70% of the screen height, or all contents.',
        default: '70vh',
        items: [
            { text: '70% of the screen height', value: '70vh' },
            { text: 'Extra Small (300px)', value: '300px' },
            { text: 'Small (500px)', value: '500px' },
            { text: 'Medium (750px)', value: '750px' },
            { text: 'Large (1000px)', value: '1000px' },
            { text: 'Show All Contents', value: 'inherit' }
        ]

    },
    visibleItemCount: {
        type: 'number',
        label: 'Visible Count of Items',
        helpText: 'Number of items that will be visible at a given time.  Additional items will be available through pagination.',
        id: 'visibleItemCount'
    },
    showPaginationAtTop: {
        type: 'boolean',
        label: 'Display Top Pagination',
        helpText: 'Number of items that will be visible at a given time.  Additional items will be available through pagination.',
        id: 'showPaginationAtTop',
        default: false
    },
    showPaginationInHeader: {
        type: 'boolean',
        label: 'Display Header Pagination',
        helpText: 'Number of items that will be visible at a given time.  Additional items will be available through pagination.',
        id: 'showPaginationInHeader',
        default: false
    },
    avatarContactCardId: {
        type: 'dropdown',
        id: 'avatarContactCardId',
        itemsSource: 'componentStore.getList(true)',
        itemsFilter: 'this.item.cType === "contactCard"',
        fields: { text: 'cId', value: 'cId' },
        label: 'Avatar Contact Card ID',
        helpText: 'Choose the Contact Card to use for the Avatar Icon.',
        logic: [
            {
                record: 'self',
                criteriaRecord: 'component',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.itemIconType.value === 'avatar'"
            }
        ]
    },
    itemIconType: {
        id: 'itemIconType',
        type: 'dropdown',
        items: [
            { text: 'None', value: 'none' },
            { text: 'Number', value: 'number' },
            { text: 'Avatar Image', value: 'avatar' },
            { text: 'First Name Initial', value: 'first_initial' },
            { text: 'Last Name Initial', value: 'last_initial' },
        ],
        fields: { text: 'text', value: 'value' },
        label: 'Main Icon for Item',
        helpText: 'Specify the icon for each item.',
        default: 'number'
    },
    itemIconSize: {
        id: 'itemIconSize',
        type: 'dropdown',
        items: [
            { text: 'Small', value: 'small' },
            { text: 'Medium', value: 'medium' },
            { text: 'Large', value: 'large' },
        ],
        fields: { text: 'text', value: 'value' },
        helpText: 'Specify the size of the item icon.',
        label: 'Item Icon Size',
        default: 'small',
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.itemIconType.value === 'number' || this.controls.itemIconType.value === 'avatar' || this.controls.itemIconType.value === 'first_initial' || this.controls.itemIconType.value === 'last_initial'"
            }
        ],
    },
    iconStyles: {
        type: 'OBJECT',
        label: 'Icon Styles',
        id: 'iconStyles',
        helpText: 'Settings specific to the Icon in your itemList',
        default: {
            backgroundColor: '#c6c6c6',
            color: '#555555'
        },
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.itemIconType.value === 'number' || this.controls.itemIconType.value === 'first_initial' || this.controls.itemIconType.value === 'last_initial'"
            }
        ],
        properties: {
            backgroundColor: {
                type: 'color',
                label: 'Icon Background Color',
                helpText: 'Color for the itemList icon.',
                id: 'backgroundColor',
                noColor: true,
                logic: [
                    {
                        record: 'self',
                        criteriaRecord: 'component',
                        prop: 'display',
                        valueType: 'criteria',
                        criteria: "this.controls.itemIconType.value === 'number' || this.controls.itemIconType.value === 'first_initial' || this.controls.itemIconType.value === 'last_initial'"
                    }
                ]
            },
            color: {
                type: 'color',
                label: 'Icon Font Color',
                helpText: 'Choose a color for the itemList Icon Font.',
                id: 'color',
                logic: [
                    {
                        record: 'self',
                        criteriaRecord: 'component',
                        prop: 'display',
                        valueType: 'criteria',
                        criteria: "this.controls.itemIconType.value === 'number' || this.controls.itemIconType.value === 'first_initial' || this.controls.itemIconType.value === 'last_initial'"
                    }
                ]
            }
        }
    },
    sortBy: {
        id: 'sortBy',
        type: 'dropdown',
        label: 'Primary Item Sort Method',
        items: [
            { label: 'record', value: 'record' },
            { label: 'item type', value: 'item type' }
        ],
        fields: {
            text: 'label',
            value: 'value'
        },
        helpText: 'Set how items should be grouped/sorted.',
    },
    sortField: {
        id: 'sortField',
        type: 'dropdowntree',
        label: 'Sort Field',
        itemsSource: 'configStore.allFieldOptions()',
        allowFiltering: true,
        filterType: 'Contains',
        fields: {
            text: 'text',
            value: 'value',
            hasChildren: 'hasChild',
            parentValue: 'pId',
        },
        helpText: 'Set which field you want to sort by.',
        logic: [
            {
                record: 'self',
                criteriaRecord: 'component',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.sortBy.value === 'record'"
            }
        ]
    },
    sortDirection: {
        type: 'dropdown',
        label: 'Direction',
        default: 'asc',
        items: [
            'asc',
            'desc'
        ],
        id: 'direction',
        logic: [
            {
                record: 'self',
                criteriaRecord: 'component',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.sortBy.value === 'record'"
            }
        ]
    },
    noRecordsMessage: {
        type: 'text',
        id: 'noRecordsMessage',
        label: 'No Records to Display Template',
        helpText: 'Write and style a custom message that will show if there are no records to display.',
        default: '<p>There are no records to display.</p>'
    }
};

let itemListConfig = {
    cId: "itemList",
    cType: "itemList",
    cTitle: "Item List",
    cTemplate: `<div v-if="!configReload && config && (!config.hideWhenNoItems || mappedItems.length)" :style="componentStyles">
                    <div class="item-list-container" :class="{'is-dragging': config.allowDragAndDrop && dragIndex !== false }" :style="itemListStyles" ref="itemList">
                        <div v-if="!config.hideListHeader" class="item-list-header" :style="headerStyles" >
                            <component :is="headerTemplate" v-if="headerTemplate" class="header-text"></component>
                            <div class="list-pagination" v-if="pages.length && config.showPaginationInHeader">
                                <div>
                                    <div class="total-page-count">Items {{startingItem}}-{{endingItem}}</div>
                                    <control name="CURRENT_PAGE" :items="pages" @update:value="currentPage = $event" class="current-page"></control>
                                </div>
                            </div>
                        </div>
                        <div v-if="config.enableFilter">
                            <condensed-filter
                                :prop-filter-options="filterOptions"
                                :prop-item-label="'records'"
                                :cId="'condensedFilter__' + cId"
                                :prop-filter-items="mappedItems"
                                prop-sub-object-key="record"
                                :prop-additional-config="config.additionalConfig"
                                :prop-result="filteredItems"
                                @update:prop-result="filteredItems = $event; filteredItemsUpdated()"
                                :prop-filtering="filtering"
                                @update:prop-filtering="filtering = $event"></condensed-filter>
                        </div>
                        <div class="list-pagination" v-if="pages.length && config.showPaginationAtTop">
                            <div>
                                <div class="total-page-count">Items {{startingItem}}-{{endingItem}}</div>
                                <control name="CURRENT_PAGE" :items="pages" @update:value="currentPage = $event" class="current-page"></control>
                            </div>
                        </div>
                        <div v-if="config.noRecordsMessage && initialLoadComplete && !itemsLength" v-html="config.noRecordsMessage" class="no-records_msg"></div>
                        <div v-if="!groupedBy">
                            <div v-if="!initialLoadComplete">
                                <div v-for="key in 3">
                                   <div class="skeleton list-item">
                                        <ejs-skeleton shape="Text" width="250px" height="2.5rem" class="header-skeleton"></ejs-skeleton>
                                        <ejs-skeleton shape="Rectangle" width="150px" height="2rem" class="button-skeleton"></ejs-skeleton>
                                   </div>
                               </div>
                            </div>
                           <div v-for="(data, index) in visibleItems"
                                :id="data.elemId"
                                class="list-item"
                                :class="[{'dragged-item': dragIndex === data.itemIndex}, data.cssClasses]"
                                :draggable="config.allowDragAndDrop && dragIndex === data.itemIndex"
                                @dragstart="onDragstart($event, data)"
                                @dragend="onDragend($event, data)"
                                @click="targetItem($event, data)"
                                :key="index"
                           >
                                <div v-if="config.allowDragAndDrop"
                                     @mousedown="dragIndex = data.itemIndex;"
                                     @mouseup="dragIndex = false;"
                                     :id="'drag_handle_' + data.elemId"
                                     class="drag-drop-handle"
                                 >
                                    <span class="e-icons e-drag-and-drop"></span>
                                 </div>
                                <div :id="data.elemId + '_drop_zone'"
                                     class="drop-zone"
                                     @drop="onDrop($event, data)"
                                     @dragover="onDragover($event, data)"
                                     @dragleave="onDragleave"
                                >
                                    <div class="drop-indicator">{{}}</div>
                                </div>
                                <div class="item-icon" :class="[ 'icon-' +config.itemIconSize, 'icon-' + config.itemIconType ]" :style="iconStyles">
                                    <span v-if="config.itemIconType === 'number'">{{data.number}}</span>
                                    <span v-if="config.itemIconType === 'first_initial' || config.itemIconType === 'last_initial'">{{data.iconInitials}}</span>
                                    <contact-card
                                        v-if="config.itemIconType === 'avatar'"
                                        c-type="contactCard"
                                        :cId="config.avatarContactCardId ? config.avatarContactCardId + '__item__' + config.cId + '__' + data.itemIndex : 'contactCard__' + data.itemIndex"
                                        :prop-contact-id="data.record.uiRecordId"
                                        :prop-contact-psp="data.record.uiRecordPsp"
                                    ></contact-card>
                                </div>
                                <div class="item-content">
                                    <div v-for="(title, iindex) in data.activeTitles" :class="title.cssClasses" :key="iindex">
                                        <component :is="title.templateId" :record="data.record" :hovered="data.hovered" :targeted="targetedItemIndex === data.itemIndex" :index="data.itemIndex" class="item-title"></component>
                                    </div>
                                    <div v-for="(description, iindex) in data.activeDescriptions" :class="description.cssClasses" :key="iindex">
                                        <component :is="description.templateId" :record="data.record" :hovered="data.hovered" :targeted="targetedItemIndex === data.itemIndex" :index="data.itemIndex"></component>
                                    </div>
                                </div>
                                <div class="item-action-area" :class="data.actionButtonDisplay">
                                    <div v-for="(action, iindex) in data.activeActions"
                                         class="item-action-item e-icons"
                                         :class="data.actionIcon"
                                         :key="action.actionId"
                                    >
                                        <control :name="action.actionId"
                                                 :key="action.actionId"
                                                 :ref="action.ref || action.id"
                                                 :actionParams="[action, data]"
                                                 data-test-id="action.dataTestId"
                                                 :class="action.cssClasses"
                                                 :disabled="action.disabled"
                                                 :label="action.label"
                                                 :style="{color:action.actionTextColor, background:action.actionBackgroundColor, borderRadius:data.actionBorderRadius}"
                                                 :prop-config="{
                                                        removeOnDestroy: true,
                                                        hideLabel: action.hideLabel,
                                                        type: 'button',
                                                        actionIcon: action.ACTION_ICON || action.actionIcon,
                                                        onclick: actionClick
                                                 }"
                                        ></control>
                                        <p v-if="action.message">{{action.message}}</p>
                                    </div>
                                </div>
                                <div class="item-action-area" v-if="data.complete">
                                    <span class="e-icons e-complete"></span>
                                    <span style="font-size:20px; font-weight: bold;">Complete</span>
                                </div>
                                <div v-if="index === visibleItems.length - 1"
                                     :id="data.elemId + '_drop_zone'"
                                     class="drop-zone last-drop-zone"
                                     @drop="onDrop($event, data, true)"
                                     @dragover="onDragover($event, data, true)"
                                     @dragleave="onDragleave"
                                >
                                    <div class="drop-indicator">{{}}</div>
                                </div>
                            </div>
                        </div>
                        <div class="grouped-items" v-if="groupedBy">
                            <div v-if="!initialLoadComplete">
                                <div v-for="key in 3"
                                 class="group-container skeleton"
                                >
                                    <div class="list-item group-header">
                                        <ejs-skeleton height="25px" width="25px" style="margin-right: 0.5rem"></ejs-skeleton>
                                        <ejs-skeleton height="1.5rem" width="300px" shape="Text"></ejs-skeleton>
                                    </div>
                                </div>
                            </div>

                            <!-------------------------------->
                            <!--  display for grouped items -->
                            <!-------------------------------->
                            <div v-if="initialLoadComplete">
                                <div v-for="(value, key) in visibleItems"
                                 :key="key"
                                 class="group-container"
                                >
                                    <div class="list-item group-header" @click="expandedGroupItems[value.group] = !expandedGroupItems[value.group]">
                                        <div style="display: flex; align-items: center; gap: 1rem">
                                            <i v-if="!expandedGroupItems[value.group]" class="fa-solid fa-chevron-right" style=""></i>
                                            <i v-if="expandedGroupItems[value.group]" class="fa-solid fa-chevron-down" style=""></i>
                                            <component v-if="config.customGroupHeaders" :is="value.headerId" :record="value.record"></component>
                                            <span v-else-if="!config.customGroupHeaders" style="line-height: 2rem">{{ value.group }}</span>
                                        </div>
                                    </div>
                                    <transition name="fade">
                                        <div v-if="expandedGroupItems[value.group] && initialLoadComplete" class="group-expanded-container">
                                            <div v-for="(data, index) in value.items"
                                                :id="data.elemId"
                                                class="grouped-item list-item"
                                                :class="[{'dragged-item': dragIndex === data.itemIndex}, data.cssClasses]"
                                                :draggable="config.allowDragAndDrop && dragIndex === data.itemIndex"
                                                @dragstart="onDragstart($event, data)"
                                                @dragend="onDragend($event, data)"
                                                @click="targetItem($event, data)"
                                                :key="index"
                                                :style="{ animationDelay: (index * 100) + 'ms' }"
                                            >
                                                <div v-if="config.allowDragAndDrop"
                                                     @mousedown="dragIndex = data.itemIndex;"
                                                     @mouseup="dragIndex = false;"
                                                     :id="'drag_handle_' + data.elemId"
                                                     class="drag-drop-handle"
                                                >
                                                    <span class="e-icons e-drag-and-drop"></span>
                                                </div>
                                                <div :id="data.elemId + '_drop_zone'"
                                                     class="drop-zone"
                                                     @drop="onDrop($event, data)"
                                                     @dragover="onDragover($event, data)"
                                                     @dragleave="onDragleave"
                                                >
                                                    <div class="drop-indicator">{{}}</div>
                                                </div>
                                                <div class="item-icon" :class="[ 'icon-' +config.itemIconSize, 'icon-' + config.itemIconType ]" :style="iconStyles">
                                                    <span v-if="config.itemIconType === 'number'">{{index + 1}}</span>
                                                    <span v-if="config.itemIconType === 'first_initial' || config.itemIconType === 'last_initial'">{{data.iconInitials}}</span>
                                                    <contact-card
                                                        v-if="config.itemIconType === 'avatar'"
                                                        c-type="contactCard"
                                                        :cId="config.avatarContactCardId ? config.avatarContactCardId + '__item__' + config.cId + '__' + data.itemIndex : 'contactCard__' + data.itemIndex"
                                                        :prop-contact-id="data.record.uiRecordId"
                                                        :prop-contact-psp="data.record.uiRecordPsp"
                                                    ></contact-card>
                                                </div>
                                                <div class="item-content">
                                                    <div v-for="(title, iindex) in data.activeTitles" :class="title.cssClasses" :key="iindex">
                                                        <component :is="title.templateId" :record="data.record" :hovered="data.hovered" :targeted="targetedItemIndex === data.itemIndex" :index="data.itemIndex" class="item-title"></component>
                                                    </div>
                                                    <div v-for="(description, iindex) in data.activeDescriptions" :class="description.cssClasses" :key="iindex">
                                                        <component :is="description.templateId" :record="data.record" :hovered="data.hovered" :targeted="targetedItemIndex === data.itemIndex" :index="data.itemIndex"></component>
                                                    </div>
                                                </div>
                                                <div class="item-action-area" :class="data.actionButtonDisplay">
                                                    <div v-for="(action, iindex) in data.activeActions"
                                                         class="item-action-item e-icons"
                                                         :class="data.actionIcon"
                                                         :key="action.actionId"
                                                    >
                                                        <control :name="action.actionId"
                                                                 :key="action.actionId"
                                                                 :ref="action.ref || action.id"
                                                                 :actionParams="[action, data]"
                                                                 data-test-id="action.dataTestId"
                                                                 :class="action.cssClasses"
                                                                 :disabled="action.disabled"
                                                                 :label="action.label"
                                                                 :style="{color:action.actionTextColor, background:action.actionBackgroundColor, borderRadius:data.actionBorderRadius}"
                                                                 :prop-config="{
                                                                        removeOnDestroy: true,
                                                                        hideLabel: action.hideLabel,
                                                                        type: 'button',
                                                                        actionIcon: action.ACTION_ICON || action.actionIcon,
                                                                        onclick: actionClick
                                                                 }"
                                                        ></control>
                                                        <p v-if="action.message">{{action.message}}</p>
                                                    </div>
                                                </div>
                                                <div class="item-action-area" v-if="data.complete">
                                                    <span class="e-icons e-complete"></span>
                                                    <span style="font-size:20px; font-weight: bold;">Complete</span>
                                                </div>
                                                <div v-if="index === visibleItems.length - 1"
                                                     :id="data.elemId + '_drop_zone'"
                                                     class="drop-zone last-drop-zone"
                                                     @drop="onDrop($event, data, true)"
                                                     @dragover="onDragover($event, data, true)"
                                                     @dragleave="onDragleave"
                                                >
                                                    <div class="drop-indicator">{{}}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </transition>
                                </div>
                            </div>
                        </div>
                        <div class="list-pagination" v-if="pages.length > 1">
                            <div>
                                <div class="total-page-count">Items {{startingItem}}-{{endingItem}}</div>
                                <control name="CURRENT_PAGE" :value="currentPage" :items="pages" @update:value="currentPage = $event" class="current-page"></control>
                            </div>
                        </div>
                    </div>
                </div>`,
    controls: [
        {
            name: 'CURRENT_PAGE',
            label: 'Current Page',
            type: 'dropdown',
            width: '110px',
            default: 1,
            showClearButton: false,
            hideLabel: true,
            fields: {
                text: 'text',
                value: 'value'
            }
        }
    ]
};

let itemListCmp = {
    name: 'itemList',
    editorSettings: editorSettings,
    dataTestIds: [
        {
            search: '.item-list .list-item',
            cData: 'itemList._index_.itemTitle',
            suffix: 'item'
        },
        {
            search: '.item-list .list-item .item-icon',
            cData: 'itemList._index_.itemTitle',
            suffix: 'title'
        },
        {
            search: '.item-list .list-item .item-title',
            cData: 'itemList._index_.itemTitle',
            suffix: 'title'
        },
        {
            search: '.item-list .list-item .item-description',
            cData: 'itemList._index_.itemTitle',
            suffix: 'description'
        },
        {
            search: '.item-list .list-item .item-action-area',
            cData: 'itemList._index_.itemTitle',
            suffix: 'action'
        }
    ],
    props: {
        relativeRecords: {
            type: Array,
            default: []
        },
        parentRecords: {
            type: Object,
            default: () => ({})
        },
        itemsList: {
            type: Array,
            default: []
        },
        selectedItem: {},
        editorVisible: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            headerTemplate: false,
            targetedItemIndex: false,
            dragIndex: false,
            expandedGroupItems: {},
            initialLoadComplete: false,
            itemsLength: 0,
            // datasetRecords: false,
            // itemTemplate: false,
            // itemTemplateId: false,
            // templatesGenerated: false,
            cachedItemProps: [],
            filtering: false,
            filteredItems: [],
            // volRecordIds: [],
            // oppRecordIds: [],
            // orgRecordIds: [],
            // allRecordIds: {},
            currentPage: 1
        };
    },
    watch: {
        currentPage(oldValue, newValue) {
            if (!newValue) this.currentPage = 1;
        }
    },
    computed: {
        filterOptions() {
            let output = {};
            if (this.config.filterOptionsList && this.config.filterOptionsList.length) {
                this.config.filterOptionsList.forEach((option) => {
                    output[option.filterKey] = {
                        label: option.label,
                        type: option.type
                    };
                });
            }
            return output;
        },
        itemListStyles() {
            return { 'max-height': this.config.itemListHeight || 'none' };
        },
        iconStyles() {
            return this.config.iconStyles;
        },
        itemList() {
            // Put workflow config stuff here
            let items = [];
            if (this.config) {
                if (this.config.itemsListSource === 'itemsList_prop') items = this.itemsList; // from props
                else if (this.config.itemList) items = this.config.itemList;
            }
            return items;
        },
        includedItems() {
            let output = [];
            let currIndex = 0;
            let mergeSubRecords = (mergeSettings, item, record) => {
                // let subRecords = record.activityAffiliations;
                let subRecords = record[mergeSettings.subSourceKey];
                if (typeof subRecords !== 'object') return record;
                if (!Array.isArray(subRecords)) subRecords = Object.values(subRecords);
                let subRecord = subRecords.find((subRecord) => {
                    let _this = {
                        subRecord,
                        record,
                        item
                    };
                    return library.eval.call(_this, mergeSettings.subRecordFilter);
                });
                if (typeof subRecord === 'object') {
                    if (mergeSettings.mergeWithParent) return library.mergeObj({}, subRecord, record);
                    record[mergeSettings.parentOutputKey] = subRecord;
                }
                return record;
            };
            let setCurrItem = (item, record) => {
                record = library.cloneObj(record);
                let newItem = library.cloneObj(item);
                if (item.enableSubRecordMerging && Array.isArray(item.subRecordMergeRules)) {
                    item.subRecordMergeRules.forEach((mergeSettings) => {
                        record = mergeSubRecords(mergeSettings, item, record);
                    });
                }
                ['vol', 'opp', 'org', 'lbe', 'sch', 'ae', 'aff'].forEach((tmpPsp) => {
                    if (item[tmpPsp + 'Id']) record[tmpPsp + 'Id'] = item[tmpPsp + 'Id'];
                });
                newItem.record = record;
                let included = true;
                if (newItem.includeWhen && newItem.includeWhen.length) {
                    included = library.logicParser.call(this, newItem.includeWhen, newItem, newItem.record);
                }
                if (typeof included === 'boolean') newItem.include = included;
                // Add the cssClasses property to all included items
                if (!newItem.cssClasses) newItem.cssClasses = '';
                if (this.editorVisible && !newItem.include) {
                    newItem.cssClasses += ' not-included';
                    newItem.include = true;
                }
                if (newItem.include) {
                    newItem.itemIndex = currIndex;
                    newItem.elemId = this.cId.replace(" ", /_/g).toLowerCase()
                        + "_list_item_" + currIndex;
                    currIndex++;
                    output.push(newItem);
                }
            };
            if (this.config.sortBy === 'record') {
                if (!Array.isArray(this.config.sortField)) this.config.sortField = [];
                const sortField = this.config.sortField.map((thisSortField) => thisSortField.split(':::')[1]);
                if (this.recordList && sortField) library.sortObject(this.recordList, sortField, this.config.sortDirection);
                this.recordList.forEach((record) => {
                    this.itemList.forEach((item) => {
                        setCurrItem(item, record);
                    });
                });
            } else {
                this.itemList.forEach((item) => {
                    this.recordList.forEach((record) => {
                        setCurrItem(item, record);
                    });
                });
            }
            this.$emit('update:includedItems', output);
            return output;
        },
        mappedItems() {
            let setCurrItem = (item, index) => {
                let record = item.record;
                let prevItem = this.includedItems[index - 1];
                let nextItem = this.includedItems[index + 1];
                let logicResult = true;
                if (item.enableWhen && item.enableWhen.length) {
                    logicResult = library.logicParser.call(this, item.enableWhen, item, item.record, prevItem, nextItem);
                    if (typeof logicResult === 'boolean') item.enable = logicResult;
                }
                if (item.activeWhen && item.activeWhen.length) {
                    logicResult = library.logicParser.call(this, item.activeWhen, item, item.record, prevItem, nextItem);
                    if (typeof logicResult === 'boolean') item.active = logicResult;
                }
                if (item.completeWhen && item.completeWhen.length) {
                    logicResult = library.logicParser.call(this, item.completeWhen, item, item.record, prevItem, nextItem);
                    if (typeof logicResult === 'boolean') item.complete = logicResult;
                }
                // Set enable, active, and complete styles
                if (item.enable) item.cssClasses += ' item-enabled';
                if (item.active) item.cssClasses += ' item-active';
                if (item.complete) item.cssClasses += ' item-complete';
                // if editorVisible is toggled on: enable, activate, and complete all items to display all buttons and messages.
                if (this.editorVisible) {
                    if (!item.enable) {
                        item.cssClasses += ' not-enabled';
                        item.enable = true;
                    }
                    if (!item.active) {
                        item.cssClasses += ' not-active';
                        item.active = true;
                    }
                    if (!item.complete) {
                        item.cssClasses += ' not-complete';
                        item.complete = true;
                    }
                }
                // Parse Actions
                let currActions = library.cloneObj(item.actions);
                if (!Array.isArray(currActions)) currActions = [];
                currActions.forEach((action, index) => {
                    action.actionId = `action-item_${this.cId.replace(" ", /_/g).toLowerCase()}_${item.itemIndex}_${index}`;
                    if (!action.cssClasses) action.cssClasses = '';
                    let included = true;
                    let disabled = false;
                    if (item.complete && action.hideWhenComplete) included = false;
                    if (!item.complete && !item.enable && action.hideWhenDisabled) included = false;
                    if (included && action.includeWhen && action.includeWhen.length) {
                        included = library.logicParser.call(this, action.includeWhen, item.actions[index], record, prevItem, nextItem);
                    }
                    if (included && action.disableWhen && action.disableWhen.length) {
                        disabled = library.logicParser.call(this, action.disableWhen, item.actions[index], record, prevItem, nextItem);
                    }
                    if (!included && this.editorVisible) {
                        action.cssClasses += ' not-included';
                        included = true;
                    }
                    if (typeof included === 'boolean') action.include = included;
                    if (typeof disabled === 'boolean') action.disabled = disabled;
                });
                let activeActions = currActions.filter((action) => action.include === true);
                // Parse Descriptions
                let currDescriptions = library.cloneObj(item.descriptions);
                if (!Array.isArray(currDescriptions)) currDescriptions = [];
                currDescriptions.forEach((description) => {
                    if (description) {
                        let included = true;
                        if (item.complete && description.hideWhenComplete) included = false;
                        if (!item.complete && !item.enable && description.hideWhenDisabled) included = false;
                        if (included && description.includeWhen && description.includeWhen.length) {
                            included = library.logicParser.call(this, description.includeWhen, description, record, prevItem, nextItem);
                        }
                        if (!included && this.editorVisible) {
                            if (!description.cssClasses) description.cssClasses = '';
                            description.cssClasses += ' not-included';
                            included = true;
                        }
                        if (typeof included === 'boolean') description.include = included;
                    }
                });
                let activeDescriptions = currDescriptions.filter((description) => description.include === true);
                // Parse Titles
                let currTitles = library.cloneObj(item.titles);
                if (!Array.isArray(currTitles)) currTitles = [];
                currTitles.forEach((title) => {
                    let included = true;
                    if (item.complete && title.hideWhenComplete) included = false;
                    if (included && !item.complete && !item.enable && title.hideWhenDisabled) included = false;
                    if (included && title.includeWhen && title.includeWhen.length) {
                        included = library.logicParser.call(this, title.includeWhen, title, record, prevItem, nextItem);
                    }
                    if (!included && this.editorVisible) {
                        if (!title.cssClasses) title.cssClasses = '';
                        title.cssClasses += ' not-included';
                        included = true;
                    }
                    if (typeof included === 'boolean') title.include = included;
                });
                let activeTitles = currTitles.filter((title) => title.include === true);
                let bgColor;
                if (!item.enableEvenOddColors) {
                    bgColor = item.itemBackgroundColor;
                } else {
                    bgColor = index % 2 === 0 ? item.evenItemBackgroundColor : item.oddItemBackgroundColor;
                }
                if (item.complete && item.itemCompleteColor) bgColor = item.itemCompleteColor;
                else if (!item.enable && item.itemDisabledColor) bgColor = item.itemDisabledColor;
                else if (item.active && item.itemActiveColor) bgColor = item.itemActiveColor;
                let newItem = library.cloneObj(item);
                newItem = {
                    ...newItem,
                    record,
                    enable: item.enable,
                    complete: item.complete,
                    active: false,
                    hovered: false,
                    selected: false,
                    activeTitles,
                    activeDescriptions,
                    activeActions,
                    itemIndex: item.itemIndex,
                    backgroundColor: bgColor,
                    actionButtonDisplay: item.itemActionDisplay,
                    actionBorderRadius: item.actionBorderRadius
                };
                if (this.config.itemIconType === 'first_initial') {
                    newItem.iconInitials = record.VOL_FIRST_NAME.substring(0, 1);
                }
                else if (this.config.itemIconType === 'last_initial') {
                    newItem.iconInitials = record.VOL_LAST_NAME.substring(0, 1);
                }
                // Run actions after everything else has been processed
                // TODO make this configurable
                if (item.onChangeActions && !this.componentPendingSubscriptions.length) {
                    let cActions = item.onChangeActions;
                    cActions.forEach((iAction) => {
                        let actionResult = true;
                        if (!iAction.runOnce || (iAction.runOnce && (!runOnceActions[this.cId] || !runOnceActions[this.cId][index]))) {
                            if (iAction.logic) {
                                actionResult = library.logicParser.call(this, iAction.logic, item, item.record, prevItem, nextItem);
                            }
                            if (typeof iAction.executeIfLogicResult !== 'undefined' && actionResult !== iAction.executeIfLogicResult) return;
                            if (!runOnceActions[this.cId]) runOnceActions[this.cId] = {};
                            // TODO: Maybe change this to make it more unique
                            if (!runOnceActions[this.cId][index]) {
                                runOnceActions[this.cId][index] = actionResult;
                                if (Array.isArray(iAction.actions)) {
                                    actionStore.runChainedActions(iAction.actions, { ...item.record, prevItem, nextItem }, this);
                                }
                            }
                        }
                    });
                }
                return newItem;
            };
            let mappedItems = this.includedItems
                .map((item, index) => setCurrItem(item, index));
            this.$emit('update:enabledItems', mappedItems.filter((iItem) => iItem.enable && !iItem.complete));
            this.$emit('update:completedItems', mappedItems.filter((iItem) => iItem.complete));
            return mappedItems;
        },
        visibleItems() {
            let currNumber = 0;
            let visibleCount = this.visibleItemCount();
            let end = this.currentPage * visibleCount;
            let start = end - visibleCount;
            if (start < visibleCount) start = 0;
            let availableRecords = this.config.enableFilter ? this.filteredItems : this.mappedItems;
            let items = availableRecords.map((item) => {
                if (!item.excludeFromCount) {
                    currNumber++;
                    item.number = currNumber;
                }
                return item;
            });
            if (items.length && this.groupedBy) {
                const [perspective, prop] = this.groupedBy[0].split(':::');
                items = this.groupItems(items, prop);
            }
            this.itemsLength = items.length;
            if (items.length) this.initialLoadComplete = true;
            const visibleItems = items.slice(start, end);
            this.$emit('update:visibleItems', visibleItems);
            return visibleItems;
        },
        datasetRecords() {
            if (this.relativeRecords && this.relativeRecords.length) return false;
            // if relativeRecords was not passed through props continue
            if (!this.config.datasetIds) return false;
            // if there are datasetIds
            let outputRecords = [];
            this.config.datasetIds.forEach((payload) => {
                // This is here to handle legacy values
                // TODO remove this after all legacy configurations have been updated
                if (typeof payload === 'string') {
                    payload = { source: [payload] };
                }
                let parentRecords = false;
                // let sDsParentRecords = {};
                let prType = this.config.parentRecordType;
                if (prType) {
                    parentRecords = {};
                    if (prType === 'parentRecords') {
                        parentRecords[1] = this.parentRecords.vol;
                        parentRecords[2] = this.parentRecords.opp;
                        parentRecords[3] = this.parentRecords.org;
                    } else {
                        let types = {
                            volId: 1,
                            oppId: 2,
                            orgId: 3
                        };
                        parentRecords[types[prType]] = [this[prType]];
                        // sDsParentRecords[prType] = this[prType];
                    }
                }
                // Configure the payload
                let sourceItem = {
                    volId: this.volId,
                    oppId: this.oppId,
                    orgId: this.orgId
                };
                // Map params to records and other contextual or static information
                let params = library.recordSourceMapper(payload.source[0], payload.params, sourceItem, this);
                // Subscribe to the data
                payload = {
                    ...payload,
                    params,
                    parentRecords
                };
                let subscription = this.recordStore.subscribe(payload, this.cId, false, false);
                let records = this.recordStore.getSubscription(subscription.key);
                // When all initialLoading is false set itemList initialLoadComplete to true (runs once)
                this.watchSubscription(subscription.key, {
                    watchProperty: 'initialLoading', // Also collectAs
                    endFnc: () => {
                        if (!this.componentSubscriptions?.initialLoading.length) {
                            setTimeout(() => {
                                // Add a little delay to prevent message blips
                                // while the data is being processed through mappedItems and other computed
                                // If there is data, initialLoadComplete will be set before this timeout runs
                                this.initialLoadComplete = true;
                            }, 5000); // NOSONAR
                        }
                    }
                });
                if (records) {
                    Object.keys(records).forEach((recordId) => {
                        outputRecords.push(records[recordId]);
                    });
                }
            });
            return outputRecords;
        },
        recordList() {
            let records = this.datasetRecords || this.relativeRecords;
            if (!Array.isArray(records) || !records.length) return [];
            return records;
        },
        headerStyles() {
            return this.config.headerStyles || {};
        },
        pages() {
            let pages = [];
            let visibleCount = this.visibleItemCount();
            let pageCount = Math.ceil(this.itemsLength / visibleCount);
            for (let i = 0; i < pageCount; i++) {
                let pageItem = {
                    text: 'Page ' + (i + 1),
                    value: i + 1
                };
                pages.push(pageItem);
            }
            return pages;
        },
        pageCount() {
            return this.pages.length;
        },
        startingItem() {
            let output = (this.currentPage * this.visibleItemCount()) - this.visibleItemCount();
            if (output < 1) output = 1;
            return output;
        },
        endingItem() {
            let output = this.currentPage * this.visibleItemCount();
            if (output < 0) output = this.visibleItemCount();
            return output;
        },
        groupedBy() {
            return this.config?.groupItemsBy?.length ? this.config?.groupItemsBy : false;
        }
    },
    methods: {
        filteredItemsUpdated() {
            this.currentPage = 1;
        },
        onDragstart(event, item) {
            event.dataTransfer.setData("application/json", JSON.stringify(item));
            event.dataTransfer.effectAllowed = "move";
        },
        onDragend(event, item) {
            this.dragIndex = false;
        },
        onDragover(event, item, lastItem) {
            event.preventDefault();
            event.target.classList.add('hovered');
            event.dataTransfer.dropEffect = "move";
        },
        onDragleave(event) {
            event.preventDefault();
            event.target.classList.remove('hovered');
        },
        onDrop(event, item, lastItem) {
            event.preventDefault();
            event.target.classList.remove('hovered');
            let movedItem = event.dataTransfer.getData("application/json");
            if (library.isJson(movedItem)) movedItem = JSON.parse(movedItem);
            let rItems = library.cloneObj(this.visibleItems);
            let spliceIndex = item.itemIndex < movedItem.itemIndex
                ? item.itemIndex
                : item.itemIndex - 1;
            if (lastItem) spliceIndex = rItems.length - 1;
            rItems.splice(movedItem.itemIndex, 1); // Remove the item from original index
            rItems.splice(spliceIndex, 0, movedItem); // Add the item back in at new index
            rItems = rItems.map((item) => item.itemIndex);
            this.$emit('update:itemOrder', { itemOrder: rItems });
            this.dragIndex = false;
        },
        // TODO: Are mouseEnter and mouseLeave still used?
        mouseEnter(item) {
            item.delayedHover = true;
            setTimeout(() => {
                if (item.delayedHover) item.hovered = true;
            }, 300);
        },
        mouseLeave(item) {
            item.delayedHover = false;
            item.hovered = false;
        },
        targetItem(event, item) {
            // Targeted items and selected items are not the same
            // There can be only one targeted item, the most recently clicked item
            this.targetedItemIndex = Array.isArray(event.value) && event.value[0];
            this.$emit('update:targetedItem', item, event);
        },
        generateId(type, itemIndex = 0, propIndex = 0, notUnique = false) {
            const cId = this.cId.replace(" ", /_/g).toLowerCase();
            let templateId = `itemTemplate_${cId}_${itemIndex}_${propIndex}_${type}`;
            if (notUnique) return templateId;
            return `${templateId}_${library.getUniqueID()}`;
        },
        createComp(template, type, itemIndex, titleIndex) {
            let iTemplateCmpId = !this.config?.groupItemsBy ? this.generateId(type, itemIndex, titleIndex, true) : this.generateId(type, itemIndex, titleIndex);
            let iTemplate = `<div>${template}</div>`;
            let component = this;
            let recordStore = this.recordStore;
            vueApp.component(iTemplateCmpId, {
                template: iTemplate,
                data() {
                    return {
                        templateData: {}, // A place to put custom, local, template specific settings
                        component: component,
                    };
                },
                props: {
                    hovered: {
                        type: Boolean,
                        default: false
                    },
                    targeted: {
                        type: Boolean,
                        default: false
                    },
                    record: {
                        type: Object,
                        default() {
                            return {};
                        }
                    },
                    index: {}
                },
                computed: {
                    moment() {
                        return library.getMomentObj();
                    },
                    actionStore() {
                        return actionStore;
                    },
                    records() {
                        return recordStore.records;
                    },
                    // These will be available fields to use in the itemList headerTemplate
                    // example: records.vol[volId].VOL_FIRST_NAME
                    // example: records.user.CLIENT_NAME
                    // example: records.opp[oppId]['CA#Application_Questions/Region_0']
                    volId() {
                        return component.contextVolId;
                    },
                    user() {
                        return component.user;
                    },
                    oppId() {
                        return component.contextOppId || component.oppId;
                    },
                    orgId() {
                        return component.contextOrgId || component.orgId;
                    },
                    aeId() {
                        return component.contextAeId || component.aeId;
                    },
                    affId() {
                        return component.contextAffId || component.affId;
                    },
                    schId() {
                        return component.contextSchId || component.schId;
                    },
                    lbeId() {
                        return component.contextLbeId || component.lbeId;
                    }
                },
                methods: {
                    trimHTML(str) {
                        return library.trimHTML(str);
                    },
                    dateDetails(dateStr, formatStr) {
                        let momentDate = library.getMoment(dateStr, formatStr);
                        return {
                            D: momentDate.format('D'),
                            MMM: momentDate.format('MMM'),
                            dddd: momentDate.format('dddd'),
                        };
                    },
                    toggleDelay(time, property) {
                        this.templateData[property] = !this.templateData[property];
                        setTimeout(() => {
                            this.templateData[property] = !this.templateData[property];
                        }, parseInt(time, 10));
                    }
                }
            });
            return iTemplateCmpId;
        },
        onPropsUpdate() {
            if (!this.itemList.length) return;
            // if (this.templatesGenerated) return;
            // this.templatesGenerated = true;
            let validTemplate = (item) => {
                let output = true;
                if (!item.template) return output;
                let interpolationItems = item.template.match(/{{(.*)}}/);
                if (interpolationItems) {
                    interpolationItems.shift();
                    for (let interpolationString of interpolationItems) {
                        if (interpolationString.indexOf('.') === -1
                            && interpolationString !== 'record'
                            && interpolationString.indexOf('record["') === -1
                            && interpolationString.indexOf("record['") === -1
                        ) {
                            // check for v-for
                            let vforRgx = 'v-for=[\'"]\\(?' + interpolationString;
                            if (!item.template.match(vforRgx, 'g')) {
                                output = false;
                            }
                            break;
                        }
                    }
                }
                return output;
            };
            let invalidTemplate = "<div>(Invalid Template)</div>";
            this.itemList.forEach((cItem, itemIndex) => {
                if (!this.cachedItemProps[itemIndex]) {
                    this.cachedItemProps[itemIndex] = { titles: [], descriptions: [] };
                }
                let cachedProp = this.cachedItemProps[itemIndex];
                if (!Array.isArray(cItem.titles)) cItem.titles = [];
                cItem.titles.forEach((cTitle, titleIndex) => {
                    if (!validTemplate(cTitle)) {
                        cTitle.template = invalidTemplate;
                    }
                    if (!cachedProp.titles[titleIndex]) cachedProp.titles[titleIndex] = { template: false, templateId: false };
                    if (cachedProp.titles[titleIndex].template === cTitle.template) {
                        cTitle.templateId = cachedProp.titles[titleIndex].templateId;
                        return;
                    }
                    cTitle.templateId = this.createComp(cTitle.template, 'title', itemIndex, titleIndex);
                    cachedProp.titles[titleIndex].template = cTitle.template;
                    cachedProp.titles[titleIndex].templateId = cTitle.templateId;
                });
                if (!Array.isArray(cItem.descriptions)) cItem.descriptions = [];
                cItem.descriptions.forEach((cDescription, descIndex) => {
                    if (!validTemplate(cDescription)) {
                        cDescription.template = invalidTemplate;
                    }
                    if (!cachedProp.descriptions[descIndex]) cachedProp.descriptions[descIndex] = { template: false, templateId: false };
                    if (cachedProp.descriptions[descIndex].template === cDescription.template) {
                        cDescription.templateId = cachedProp.descriptions[descIndex].templateId;
                        return;
                    }
                    cDescription.templateId = this.createComp(cDescription.template, 'description', itemIndex, descIndex);
                    cachedProp.descriptions[descIndex].template = cDescription.template;
                    cachedProp.descriptions[descIndex].templateId = cDescription.templateId;
                });
            });
        },
        async actionClick(event, control, actionParams) {
            const [action, item] = actionParams;
            let response;
            // Perform an action
            let actionConfig = library.cloneObj(action.action);
            let prevItem = this.visibleItems[item.itemIndex - 1];
            let nextItem = this.visibleItems[item.itemIndex + 1];
            if (Array.isArray(actionConfig)) {
                response = await actionStore.runChainedActions(actionConfig, { ...item.record, prevItem, nextItem }, this);
            }
            else {
                if (actionConfig.action) {
                    let payload = library.recordSourceMapper(actionConfig.action, actionConfig.params, { ...item.record, prevItem, nextItem }, this, true);
                    if (payload) response = await actionStore[actionConfig.action](payload);
                }
            }
            return response;
        },
        visibleItemCount() {
            return this.config.visibleItemCount || 100; // NOSONAR
        },
        groupItems(items, prop) {
            const grouped = items.reduce((acc, item) => {
                const key = item?.record?.[prop];
                if (!acc[key]) {
                    acc[key] = {
                        group: key,
                        items: [],
                        record: item.record,
                        headerId: this.createComp(this.config.txtGroupHeading, 'group_header', 1, 1)
                    };
                }
                acc[key].items.push(item);
                return acc;
            }, {});

            return Object.values(grouped);
        },
        onLoad() {
            this.headerTemplate = this.createComp(this.config?.header, 'itemListHeader');
        }
    },
    components: {
        condensedFilter: defineAsyncComponent(() => import('@common/condensedFilter/condensedFilter.vue')),
        'ejs-skeleton': SkeletonComponent
    }
};

componentStore.initComponent(itemListCmp, itemListConfig, '@common/itemList.vue');
export { itemListConfig, itemListCmp, editorSettings };
export default itemListCmp;
</script>

<style scoped>
.item-list-container {
    border: 1px solid #e0e0e0;
    background: #fff;
    padding: 5px;
    overflow: auto;
    position: relative;
}
.item-list-header {
    font-size: 20px;
    font-weight: bold;
    color: black;
    padding: 5px;
    border-bottom: 1px solid #e0e0e0;
}
.item-list-header, .item-list-footer {
    display: flex;
    flex-wrap: wrap;
}
.item-list-header .header-text {
    flex-grow: 9;
}
ul.item-list {
    margin-top: 0;
    padding-left: 0;
}
.list-item {
    display: flex;
    flex-direction: row;
    background: rgba(0,0,0, 0.03);
    font-size: 16px;
    border-bottom: 1px solid #e0e0e0;
    overflow: hidden;
    position: relative;
}
.group-container {
    margin: 0.5rem 0;
}
.group-expanded-container {
    border: 1px solid #e0e0e0;
    border-top: none;
    padding: 0.5rem;
}
.list-item.group-header {
    display: flex;
    align-items: center;
    font-weight: 500;
    padding: 1rem;
    cursor: pointer;
}
.list-item.group-header i {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1rem;
    min-width: 15px;
}
.list-item.group-header .square-skeleton {
    margin-right: 1rem;
}
.list-item.skeleton {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.header-skeleton {
    margin: 1rem 2rem;
}
.button-skeleton {
    margin: 1.5rem 2rem;
}
.list-item:nth-child(even) {
    /* background: rgba(0,0,0, 0.045); */
}
.item-icon {
    border-radius: 50%;
    font-weight: bold;
    line-height: 33px;
    text-align: center;
}
.item-icon.icon-none {
    display: none;
}
.item-icon.icon-small {
    width: 32px;
    height: 32px;
    padding-top: 0;
    font-size: 18px;
}
.item-icon.icon-medium {
    width: 64px;
    height: 64px;
    font-size: 22px;
    padding-top: 8px;
}
.item-icon.icon-large {
    width: 96px;
    height: 96px;
    padding-top: 12px;
    font-size: 32px;
}
.item-icon.icon-large.icon-avatar, .item-icon.icon-medium.icon-avatar {
    padding-top: 0;
}
.item-icon.icon-number {
    background: #e3f1fc;
}
.item-title {
    z-index: 3;
}
.item-content {
    flex-grow: 9;
    padding: 0 10px 0 10px;
}
.item-content p {
    margin: 6px 0;
    font-size: 0.85rem;
}
.item-action-area {
    align-items: center;
}
.item-action-item {
    text-align: right;
    margin: 5px 0 5px 10px;
}
.item-action-item button {
    display: flex;
}
.item-complete {
    font-size: 16px;
    background: #c8e5f8;
}
.item-disabled {
    position: relative;
    font-size: 14px;
    background: #ddd;
}
.item-disabled.list-item:nth-child(even) {
    background: #ddd;
}
.item-disabled::after {
    content: "";
    display: block;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    position: absolute;
    background: white;
    cursor: not-allowed;
    opacity: 0.25;
}
.action-btn-inline {
    display: flex;
    align-items: flex-end;
}
/*.action-icon.e-icons {
    font-size: 1.25rem;
}*/
:deep(.e-icons.e-complete:before) {
    content: '\e192' !important;
}
.e-icons.e-edit:before {
    content: '\e944' !important;
}
.e-icons.e-email:before {
    content: '\e816' !important;
}
.e-icons.e-eye:before {
    content: '\e345' !important;
}
.e-icons.e-mouse-pointer:before {
    content: '\ec1d' !important;
}
.e-icons.e-circle-info:before {
    content: '\e607' !important;
}
.e-icons.e-delete-5:before {
    content: '\e706' !important;
}
.list-pagination {
    text-align: right;
    margin: 3px 0;
}
.list-pagination > div {
    display: inline-block;
    padding: 3px 8px 3px 8px;
    line-height: 0;
    border-radius: 5px;
    background: rgba(255, 255, 255, .7);
    border: 1px solid rgba(0,0,0, 0.3);
    box-shadow: 1px 1px 10px rgb(0 0 0 / 10%);
}
.list-pagination .current-page {
    display: inline-block;
    margin-left: 10px;
}
.list-pagination .total-page-count{
    display: inline-block;
    font-size: 16px;
    color: #000;
    font-family: sans-serif;
}
.list-pagination :deep(.e-input-group, .e-input-group.e-control-wrapper) {
    margin-bottom: 0;
}
/* Drag & Drop Classes */
.drag-drop-handle {
    width: 20px;
    height: auto;
    margin: 2px;
    background: rgb(238, 117, 137);
    opacity: 0.2;
    color: #5e0a19;
    cursor: grab;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    border-radius: 2px;
    transition: opacity .3s ease-in-out;
    text-shadow: -1px -1px 2px #ffd4e0, 1px 1px 2px black;
}
.list-item:hover .drag-drop-handle {
    opacity: 0.7;
}
.list-item:hover .drag-drop-handle:hover {
    opacity: 1;
}
.dragged-item .drag-drop-handle {
    cursor: grabbing;
}
.e-drag-and-drop::before {
    content: "\e903" !important;
    font-family: "e-icons" !important;
}
.list-item.dragged-item {
    border: 2px dashed rgb(229, 88, 113) !important;
}
.drop-zone {
    position: absolute;
    width: 100%;
    height: 40px;
    padding-top: 18px;
    left: 0;
    top: -20px;
    display: none;
    background: rgba(0,0,0, 0.1);
}
.drop-zone.last-drop-zone {
    height: 20px;
    top: auto;
    left: 0;
    bottom: 0;
}
.drop-indicator {
    height: 4px;
    background: rgb(229, 88, 113);
    position: relative;
    z-index: 1000;
    width: 102%;
    border-radius: 0 2px 2px 0;
    display: none;
    pointer-events: none; /* used to prevent dragleave when over indicator */
}
/* :hover pseudo class does not work when holding mouse click down */
.drop-zone.hovered .drop-indicator {
    display: block;
}
.drop-indicator:before {
    display: inline-block;
    content: ' ';
    width: 0;
    height: 0;
    position: relative;
    left: -5px;
    top: -8px;
    border-top: 7px solid transparent;
    border-bottom: 7px solid transparent;
    border-left: 12px solid rgb(229, 88, 113);
}
.is-dragging .drop-zone {
    display: block;
}
/* Prevent display of the drop-zone of the item being dragged, and it's next sibling's drop-zone */
.is-dragging .dragged-item .drop-zone, .is-dragging .dragged-item + .list-item .drop-zone:not(.last-drop-zone) {
    display: none;
}
.is-dragging .list-item {
    overflow: inherit;
}
.item-list-container.is-dragging {
    position: relative !important;
    overflow: inherit;
    overflow-x: visible !important;
}

.no-records_msg {
    background: #eee;
    border: 1px solid #d3d3d3;
    border-radius: 4px;
    margin: 1rem;
    padding: 1rem 2.5rem;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 300ms ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}
.grouped-item.list-item {
    opacity: 0;
    transform: translateX(4rem);
    animation: fadeIn 250ms forwards;
}
@keyframes fadeIn {
    to {
        transform: translateX(0);
        opacity: 1;
    }
}
</style>
